import React, {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useState,
} from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import AppleIcon from '@mui/icons-material/Apple';
import { environment } from '../../../environments/environment';

const SCRIPT_URL = environment.squareScriptUrl;


const SquareApplePay = forwardRef(function SquareApplePay(
  {
    applicationId = environment.squareApplicationId,
    currentStoreId = undefined,
    orderTotal = 0,
    onToken,
    onError,
    // Optional: Enhanced payment request details
    paymentRequestDetails = {},
    // Optional: Enable SCA verification
    enableVerifyBuyer = true,
  },
  ref
) {
  const effectiveLocationId = currentStoreId;
  const applePayRef = useRef(null);
  const paymentsRef = useRef(null);
  const paymentRequestRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const isProcessingRef = useRef(false); // Use ref to track processing state

  const [ready, setReady] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [initError, setInitError] = useState(null);
  const [isApplePayAvailable, setIsApplePayAvailable] = useState(false);


  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Don't re-initialize if payment was already successful
    if (paymentSuccess) {
      return;
    }

    let mounted = true;

    const loadScript = async () => {
      try {
        // Check if Apple Pay is available on this device
        if (
          !window.ApplePaySession ||
          !window.ApplePaySession.canMakePayments()
        ) {
          setInitError('Apple Pay is not available on this device');
          setIsApplePayAvailable(false);
          return;
        }

        setIsApplePayAvailable(true);

        // Load Square SDK if not already loaded
        if (!window.Square) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = SCRIPT_URL;
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if (!mounted) return;

        // Initialize Square Payments
        const payments = window.Square.payments(
          applicationId,
          effectiveLocationId
        );
        paymentsRef.current = payments;

        // Create Payment Request
        const amountInCents = Math.round(orderTotal);
        const amountFormatted = (amountInCents / 100).toFixed(2);

        // Build payment request with base configuration and optional enhancements
        const paymentRequestConfig = {
          countryCode: 'US',
          currencyCode: 'USD',
          total: {
            amount: amountFormatted,
            label: paymentRequestDetails.label || 'Total',
          },
          // Optional: Add line items, shipping, etc. from paymentRequestDetails
          ...(paymentRequestDetails.lineItems && {
            lineItems: paymentRequestDetails.lineItems,
          }),
          ...(paymentRequestDetails.requestShippingContact && {
            requestShippingContact:
              paymentRequestDetails.requestShippingContact,
          }),
          ...(paymentRequestDetails.requestBillingContact && {
            requestBillingContact: paymentRequestDetails.requestBillingContact,
          }),
        };

        const paymentRequest = payments.paymentRequest(paymentRequestConfig);
        paymentRequestRef.current = paymentRequest;

        // Initialize Apple Pay with Payment Request
        const applePay = await payments
          .applePay(paymentRequest)
          .catch((error) => {
            throw new Error(
              'Apple Pay is not configured for this merchant. Please use a credit card instead.'
            );
          });

        applePayRef.current = applePay;
        setReady(true);
        setInitError(null);
      } catch (err) {
        let errorMessage = 'Failed to initialize Apple Pay';
        if (err.message) {
          errorMessage = err.message;
        }
        if (err.message && err.message.includes('not available')) {
          errorMessage =
            'Apple Pay is not available for this location or device';
        }
        if (
          err.message &&
          (err.message.includes('merchant') ||
            err.message.includes('configured'))
        ) {
          errorMessage =
            'Apple Pay is not currently available. Please use a credit card.';
        }

        setInitError(errorMessage);
        onError?.(err);
      }
    };

    loadScript();

    return () => {
      mounted = false;
      // Only destroy if not processing a payment to avoid "already been destroyed" error
      if (!isProcessingRef.current && applePayRef.current) {
        try {
          applePayRef.current.destroy();
        } catch (e) {
          // Error handled silently
        }
      }
    };

  }, [applicationId, effectiveLocationId, orderTotal, onError, paymentSuccess, paymentRequestDetails]);


  useImperativeHandle(
    ref,
    () => ({
      tokenize: async () => {
        if (!applePayRef.current) {
          throw new Error('Apple Pay not initialized');
        }

        setIsProcessing(true);

        try {
          const result = await applePayRef.current.tokenize();
          setIsProcessing(false);

          if (result.status === 'OK') {
            const token = result.token;

            // Apply SCA verification if enabled
            if (enableVerifyBuyer && paymentsRef.current) {
              try {
                const verificationDetails = {
                  amount: (Math.round(orderTotal) / 100).toFixed(2),
                  billingContact: result.details?.billingContact,
                  currencyCode: 'USD',
                  intent: 'CHARGE',
                };

                const verificationResults =
                  await paymentsRef.current.verifyBuyer(
                    token,
                    verificationDetails
                  );

                // Return both token and verification token
                const enhancedToken = {
                  ...token,
                  verificationToken: verificationResults.token,
                };

                onToken?.(enhancedToken);
                return enhancedToken;
              } catch (verifyError) {
                // Continue with payment even if SCA fails (fallback behavior)
                // In production, you might want to enforce SCA
                onToken?.(token);
                return token;
              }
            } else {
              // SCA disabled or not available
              onToken?.(token);
              return token;
            }
          }

          const message =
            (result.errors && result.errors[0] && result.errors[0].message) ||
            'Apple Pay tokenization failed';
          const err = new Error(message);
          onError?.(err);
          throw err;
        } catch (error) {
          setIsProcessing(false);
          onError?.(error);
          throw error;
        }
      },
      isReady: () => ready,
    }),
    [ready, onToken, onError, enableVerifyBuyer, orderTotal]
  );

  if (!applicationId || !effectiveLocationId) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost' }}>
          Apple Pay configuration is missing. Please check your environment
          settings or provide a currentStoreId.
        </Typography>
      </Box>
    );
  }

  if (!isApplePayAvailable && initError) {
    return (
      <Box sx={{ padding: '20px', textAlign: 'center' }}>
        <Typography color="error" sx={{ fontFamily: 'Jost', fontSize: '14px' }}>
          {initError}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Jost',
            fontSize: '12px',
            color: '#666',
            mt: 1,
          }}
        >
          Apple Pay is only available on Apple devices (iPhone, iPad, Mac with
          Safari).
        </Typography>
      </Box>
    );
  }

  if (initError && isApplePayAvailable) {
    return (
      <Box
        sx={{
          padding: '20px',
          textAlign: 'center',
          backgroundColor: '#FFF9E6',
          borderRadius: '12px',
          border: '1px solid #FFE0B2',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Jost',
            fontSize: '14px',
            color: '#E65100',
            fontWeight: 500,
          }}
        >
          {initError}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Success Message */}
      {paymentSuccess && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '30px 20px',
            gap: 2,
            backgroundColor: '#f0f9ff',
            borderRadius: '12px',
            border: '2px solid #22c55e',
          }}
        >
          <Box
            sx={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '8px',
            }}
          >
            <Typography
              sx={{
                fontSize: '32px',
                color: 'white',
              }}
            >
              ✓
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '18px',
              fontWeight: 600,
              color: '#16a34a',
            }}
          >
            Payment Method Authorized!
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '14px',
              color: '#666',
              textAlign: 'center',
            }}
          >
            Your Apple Pay payment method has been authorized. Payment will be processed when your order is confirmed.
          </Typography>
        </Box>
      )}

      {/* Loading State */}
      {!ready && !paymentSuccess && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 20px',
            gap: 2,
          }}
        >
          <CircularProgress size={32} sx={{ color: environment.mainColor }} />
          <Typography
            sx={{
              fontFamily: 'Jost',
              fontSize: '14px',
              color: '#666',
            }}
          >
            Loading Apple Pay...
          </Typography>
        </Box>
      )}

      {ready && !paymentSuccess && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button
            variant="contained"
            onClick={async () => {
              // Check processing state from ref to avoid dependency issues
              if (isProcessingRef.current) {
                return;
              }

              // Show processing overlay at start: while the Apple Pay sheet is open it will be on top;
              // when the user finishes and the sheet closes, this overlay is visible during
              // "getting the nonce" so the user knows to wait.
              isProcessingRef.current = true;
              setIsProcessing(true);

              try {
                const tokenResult = await applePayRef.current.tokenize();

                if (tokenResult.status === 'OK') {
                  let finalToken = tokenResult.token;

                  // Apply SCA verification if enabled
                  if (enableVerifyBuyer && paymentsRef.current) {
                    try {
                      const verificationDetails = {
                        amount: (Math.round(orderTotal) / 100).toFixed(2),
                        billingContact: tokenResult.details?.billingContact,
                        currencyCode: 'USD',
                        intent: 'CHARGE',
                      };

                      const verificationResults =
                        await paymentsRef.current.verifyBuyer(
                          tokenResult.token,
                          verificationDetails
                        );

                      // Enhance token with verification token
                      finalToken = {
                        ...tokenResult.token,
                        verificationToken: verificationResults.token,
                      };
                    } catch (verifyError) {
                      // Continue with payment even if SCA fails (fallback behavior)
                      // In production, you might want to enforce SCA and reject payment
                    }
                  }

                  setPaymentSuccess(true); // Mark payment as successful
                  // Call onToken callback - parent component will handle the token processing
                  onToken?.(finalToken);
                } else {
                  const errorMsg =
                    tokenResult.errors?.[0]?.message || 'Tokenization failed';
                  isProcessingRef.current = false;
                  setIsProcessing(false);
                  onError?.(new Error(errorMsg));
                }
              } catch (error) {
                // Don't show processing overlay for errors - user cancelled or error occurred
                isProcessingRef.current = false;
                setIsProcessing(false);
                onError?.(error);
              }
            }}
            sx={{
              width: '100%',
              backgroundColor: '#000',
              color: '#fff',
              padding: '14px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'Jost, sans-serif',
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              '&:hover': {
                backgroundColor: '#333',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              },
            }}
            disabled={isProcessing}
          >
            <AppleIcon sx={{ fontSize: '24px' }} />
            {isProcessing ? 'Processing...' : 'Pay with Apple Pay'}
          </Button>
        </Box>
      )}

      {/* Processing Overlay */}
      {isProcessing && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#fff',
              padding: '32px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '300px',
            }}
          >
            <CircularProgress
              size={48}
              sx={{ color: environment.mainColor, marginBottom: '16px' }}
            />
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '18px',
                fontWeight: 600,
                color: '#1A2341',
                textAlign: 'center',
                marginBottom: '8px',
              }}
            >
              Processing Apple Pay Payment...
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Jost',
                fontSize: '14px',
                color: '#7A869A',
                textAlign: 'center',
              }}
            >
              Please wait while we securely process your payment
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
});

export default SquareApplePay;
